"use client";
import { useTheme } from "@mui/material/styles";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import Logo from "@/app/components/Logo";
import { useRouter } from "next/navigation";

const DashboardCard = ({
  title,
  subtitle,
  children,
  action,
  footer,
  height,
  backgroundColor,
  borderRadius = "10px",
  onClose,
  onExpand,
  showCloseIcon = false,
  showExpandIcon = false,
  expandUrl,
  expandTooltip,
  closeTooltip,
  titleVariant = "h4",
  subtitleVariant = "subtitle2",
  titleSx = {},
  logo,
  shadow,
  border,
  minHeight,
  noPadding = false,
  noIcon = false,
  expandIconSx,
  sx = {},
}) => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleIconClick = () => {
    if (showCloseIcon && onClose) {
      onClose();
    } else if (showExpandIcon && expandUrl) {
      router.push(expandUrl);
    }
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
        border: border ? `1px solid ${theme.palette.divider}` : "none",
        height: height || "auto",
        minHeight: minHeight || 370,
        position: "relative",
        borderRadius,
        backgroundColor: backgroundColor || theme.palette.background.paper,
        boxSizing: "border-box",
        width: { xs: "94%", sm: "100%" },
        margin: "auto",
        ...sx,
      }}
      elevation={shadow ? 1 : 0}
    >
      {(showCloseIcon || showExpandIcon) && (
        <Tooltip
          title={
            showCloseIcon
              ? closeTooltip || ""
              : showExpandIcon
              ? expandTooltip || ""
              : ""
          }
        >
          <IconButton
            onClick={handleIconClick}
            sx={{
              position: "absolute",
              top: isMobile ? 6 : 8,
              right: isMobile ? 0 : 8,
              zIndex: 1000,
              padding: 1,
              ...expandIconSx,
            }}
          >
            {showCloseIcon ? (
              <CloseIcon />
            ) : showExpandIcon ? (
              <ArrowOutwardIcon />
            ) : null}
          </IconButton>
        </Tooltip>
      )}
      <CardContent
        sx={{
          p: noPadding ? 0 : isMobile ? 1 : 2,
          pt: noPadding ? 0 : noIcon ? (isMobile ? 3 : 3) : isMobile ? 6 : 2,
          display: "flex",
          flex: 1,
          flexDirection: "column",
        }}
      >
        {title && (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              {logo && (
                <Box sx={{ mb: 4 }}>
                  <Logo width={70} height={70} />
                </Box>
              )}
              <Typography
                variant={titleVariant}
                sx={{
                  maxWidth: isMobile ? "400px" : "100%",
                  overflow: "hidden",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                  textOverflow: isMobile ? "clip" : "ellipsis",
                  lineHeight: 1.5,
                  ...titleSx,
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant={subtitleVariant}
                  color="textSecondary"
                  sx={{
                    mt: 1,
                    maxWidth: isMobile ? "100%" : "100%",
                    overflow: "hidden",
                    whiteSpace: isMobile ? "normal" : "nowrap",
                    textOverflow: isMobile ? "clip" : "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: isMobile ? 6 : 3,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action && (
              <Box sx={{ flex: "0 1 50%", maxWidth: "50%" }}>{action}</Box>
            )}
          </Stack>
        )}
        {children}
      </CardContent>
      {footer}
    </Card>
  );
};

export default DashboardCard;
